import pick from 'lodash/pick';
import React, { useMemo, useRef, useState } from 'react';
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';
import { useTranslation } from 'react-i18next';
import FieldContainer from '../../components/FieldContainer';
import { SELECT_CLASS_PREFIX } from '../../components/FixedRequiredSelect';
import StringSummary from '../../components/StringSummary';
import { hasOriginalData } from '../../utils';
import { GooglePlace } from '../CarbonFootprintTransportField/carbon-footprint.types';
import { FieldProps } from '../field.types';
import { getPlaceInfos } from './utils';

type GeoFieldProps = FieldProps<Record<string, any>> & {
  options?: {
    country?: string;
    type?: 'city' | 'station' | 'address';
  };
  strings?: {
    emptyResults?: string;
  };
};

function getAutocompletionTypes(mode?: string): string[] {
  switch (mode) {
    case 'city':
      return ['(cities)'];
    case 'station':
      return ['airport', 'train_station', 'subway_station', 'bus_station', 'transit_station'];
    case 'address':
    default:
      return ['geocode'];
  }
}

export const GeoField = (props: GeoFieldProps) => {
  const { t, i18n } = useTranslation();
  const { name, required, value, options, onChange, placeholder, mode, disabled, locked, strings } = props;
  const { country, type } = options || {};
  const autocompletionTypes = useMemo(() => getAutocompletionTypes(type), [type]);
  const [inputValue, setInputValue] = useState('');
  const inputValueRef = useRef('');
  // The suggestions in the language of the form. Not through `apiOptions`: the library reads it only when it loads
  // the Google script itself (with an `apiKey`), and the page loads it first. Its type omits `language`, but it
  // hands the whole request to Google
  const autocompletionRequest = {
    types: autocompletionTypes,
    componentRestrictions: country ? { country: country.toLowerCase() } : undefined,
    language: i18n.language,
  };

  const handleChange = async (place: { value: GooglePlace } | null) => {
    if (!place || !place.value?.place_id) {
      onChange(name, null);
      return;
    }
    const geo = await geocodeByPlaceId(place.value.place_id);
    const standardPlace = pick(place.value, ['description', 'place_id', 'reference']);
    onChange(name, geo.length > 0 ? getPlaceInfos(geo[0], standardPlace) : standardPlace);
  };

  // What the participant typed, pasted or had the phone keyboard fill. The list would clear it when the field loses
  // focus: kept instead, and saved as the address when no suggestion was picked
  const changeInputValue = (text: string) => {
    // Read at once: on a touch screen, picking a suggestion clears the text and leaves the field in the same event,
    // before a new render
    inputValueRef.current = text;
    setInputValue(text);
  };

  const handleInputChange = (text: string, { action }: { action: string }) => {
    if (action === 'input-change') changeInputValue(text);
    if (action === 'set-value') changeInputValue('');
  };

  const handleBlur = () => {
    const text = inputValueRef.current.trim();
    if (text && text !== value?.description) onChange(name, { description: text });
    changeInputValue('');
  };

  if (mode === 'summary') {
    return <StringSummary {...props} value={value?.description} type="StringField" />;
  }

  return (
    <FieldContainer {...props} type="GeoField">
      <GooglePlacesAutocomplete
        autocompletionRequest={autocompletionRequest}
        selectProps={{
          classNamePrefix: SELECT_CLASS_PREFIX,
          required,
          isDisabled: disabled || (locked && hasOriginalData(name)),
          name,
          noOptionsMessage: () => {
            return strings?.emptyResults || t('geo.no-options');
          },
          value: value ? { label: value.description, value: value } : undefined,
          placeholder: placeholder || t('geo.trip-placeholder'),
          isClearable: true,
          onChange: handleChange as any,
          inputValue,
          onInputChange: handleInputChange as any,
          onBlur: handleBlur,
        }}
      />
    </FieldContainer>
  );
};
